import { styled, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { getSenderInitials } from '../MessageListItem/useMessageListItem';

type Props = {
  sender: string;
  address?: string;
  /** Human date, e.g. "Reçu le jeudi 9 octobre 2026 à 9h00". */
  date: string;
  /** Machine-readable date (ISO 8601). */
  dateTime: string;
  /** Controls related to the message display (view mode switch…). */
  action?: ReactNode;
  'data-testid'?: string;
};

const AVATAR_SIZE = 40;

const Root = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
}));

const Avatar = styled('span')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.elevation1,
  color: theme.palette.text.secondary,
}));

const Identity = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Ellipsis = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const SenderName = styled('p')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const Action = styled('div')({
  flexShrink: 0,
});

/** Sender identity and reception date at the top of a displayed message. */
export function MessageHeader(props: Props) {
  const showAddress = Boolean(props.address && props.address !== props.sender);

  return (
    <Root data-testid={props['data-testid'] ?? 'message-header'}>
      <Avatar aria-hidden="true">{getSenderInitials(props.sender)}</Avatar>
      <Identity>
        <SenderName>{props.sender}</SenderName>
        {showAddress ? (
          <Ellipsis variant="body2" color="text.secondary">
            {props.address}
          </Ellipsis>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          <time dateTime={props.dateTime}>{props.date}</time>
        </Typography>
      </Identity>
      {props.action ? <Action>{props.action}</Action> : null}
    </Root>
  );
}

export type { Props as MessageHeaderProps };
